import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { Patient } from '@stockcare-storage/patient/patient';
import { Router } from '@angular/router';
import { HeaderService } from '@stockcare-services/header-service/header.service';
import { PatientStatus } from '@stockcare-models/PatientStatus';
import { TheatrePatient } from '@stockcare-storage/theatre-patient/theatre-patient';
import { IBed } from '@stockcare-models/IBed';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';
import { WhoChecklistService } from '@stockcare-services/who-checklist-service/who-checklist.service';
import { WhoChecklist } from '@stockcare-models/WhoChecklist';
import { MatDialog } from '@angular/material/dialog';
import { SurgicalSafetyChecklistDialogComponent } from '@stockcare-views/theatre-care/surgical-safety-checklist-dialog/surgical-safety-checklist-dialog.component';
import { ChecklistProgressStorageService } from '@stockcare-storage/checklist/checklist-progress-storage.service';
import { TheatrePatientStorageService } from '@stockcare-storage/theatre-patient/theatre-patient-storage.service';
import { TheatrePatientService } from '@stockcare-services/theatre-patient-service/theatre-patient-service';
import { Observable } from 'rxjs';
import { QueueType } from '@stockcare-models/QueueType';
import { QueueStorageService } from '@stockcare-storage/queue/queue-storage.service';

@Component({
  standalone: false,
  selector: 'app-patient-card',
  templateUrl: './patient-card.component.html',
  styleUrls: ['./patient-card.component.scss'],
})
export class PatientCardComponent implements OnInit {
  isInTheatre: boolean = false;
  isInTheatreRecovery: boolean = false;
  isPendingSubmit: boolean = false;
  submittingToSapDate: Date;
  showCheckListProgressBar: boolean = false;
  @Input() patient: Patient & TheatrePatient;
  @Input() discharged: boolean;
  @Input() type: string;
  @Input() overrideSelectPatientRoute: false;
  @Input() bed: IBed;
  @Input() isTisScoring: boolean = false;
  @Output() patientSelected: EventEmitter<any> = new EventEmitter<any>();
  currentStep: number = 0;
  totalSteps: number = 0;
  constructor(
    private router: Router,
    private headerService: HeaderService,
    private settingsService: SettingsService,
    private whoChecklistService: WhoChecklistService,
    private dialog: MatDialog,
    private checklistProgressStorageService: ChecklistProgressStorageService,
    private theatrePatientStorageService: TheatrePatientStorageService,
    private theatrePatientService: TheatrePatientService,
    private queueStorageService: QueueStorageService
  ) {}

  ngOnInit() {
    this.showCheckListProgressBar = this.settingsService.getBooleanSetting(Settings.ENABLE_CHECKLIST);
    if (this.showCheckListProgressBar) {
      this.getCheckListProgress();
    }
    this.buildIsInTheatre();
    if (this.patient.PatientStatus == PatientStatus.InTheatreOperatingRoom) {
      this.buildTimeInOperatingRoom();
    }
    if (this.patient.PatientStatus == PatientStatus.TheatreVisitCompletePendingSubmission) {
      this.buildPatientSubmitToSAPDate();
    }
  }

  private buildIsInTheatre() {
    if (this.patient) {
      this.isInTheatre = PatientStatus.isInTheatre(this.patient.PatientStatus);
      this.isInTheatreRecovery = PatientStatus.isInTheatreRecovery(this.patient.PatientStatus);
      this.isPendingSubmit = PatientStatus.isPendingSubmit(this.patient.PatientStatus);
    }
  }

  private buildTimeInOperatingRoom() {
    var endDate = new Date();
    var startDate = new Date(this.patient.OperatingStartDate);
    var seconds = (endDate.getTime() - startDate.getTime()) / 1000;
    this.patient.TimeInOperatingRoomSeconds = seconds;
  }

  selectPatient() {
    if (this.overrideSelectPatientRoute) {
      if (this.type == "theatre-patient-card" && this.showCheckListProgressBar && this.currentStep === 0) {
        this.openSurgicalSafetyChecklistDialog();
        return;
      }
      this.patientSelected.next(this.patient);
      return;
    }

    let routeTo = 'basket';
    this.headerService.navUrl = 'app/patients';
    if (this.discharged) {
      routeTo = 'history';
      this.headerService.navUrl = 'app/patients/discharged/true';
    }

    this.router.navigate([`app/patients/${routeTo}`], {
      queryParams: { caseNumber: this.patient.CaseNumber },
    });


  }

  buildPatientSubmitToSAPDate() {
    var theatreSubmitTime = this.settingsService.getSettingNumber("delayTheatreBasketSubmissionTime");
    var moment = require('moment');
    this.submittingToSapDate = new moment(this.patient.EndDate).add(theatreSubmitTime, 's').toDate();
  }

  getCheckListProgress() {
    if (!this.patient || !this.patient.Id) {
      return;
    }

    this.whoChecklistService.ensureChecklistIsLoaded(this.patient.Id)
      .subscribe(
        () => {
          this.loadChecklistProgress();
        },
        () => {
          this.loadChecklistProgress();
        }
      );
  }

  loadChecklistProgress() {
    this.checklistProgressStorageService.getChecklistProgressById(this.patient.Id).subscribe(async (result) => {
      if (result) {
        this.currentStep = parseInt(result.split('|')[0]);
        this.totalSteps = parseInt(result.split('|')[1]);
      }
    });
  }

  refreshChecklistProgress() {
    if (this.showCheckListProgressBar) {
      this.getCheckListProgress();
    }
  }

  openSurgicalSafetyChecklistDialog() {
    const dialogReference = this.dialog.open(SurgicalSafetyChecklistDialogComponent, {
      data: {
        theatrePatient: this.patient as TheatrePatient,
        currentStep: this.currentStep,
        totalSteps: this.totalSteps
      },
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: 'calc(100vw - 10px)',
      height: 'calc(100vh - 65px)',
      position: {
        bottom: '5px',
        left: '5px',
        right: '5px',
        top: '60px',
      },
      panelClass: 'surgical-safety-checklist-dialog-container',
      disableClose: true,

    });

    dialogReference.afterClosed().subscribe((result) => {
      if (result) {
        this.getCheckListProgress();

        if (result.hasChanges === true) {
          this.saveChanges(result.updatedTheatrePatient)
            .subscribe(() => {
              console.log('[PatientCardComponent] Changes saved successfully');
            }, (error) => {
              console.error('[PatientCardComponent] Error saving changes:', error);
            });
        }

        if (this.currentStep > 0) {
          this.proceedWithPatientSelection();
        }
      }
    });
  }

  private proceedWithPatientSelection() {
    let routeTo = 'basket';
    this.headerService.navUrl = 'app/patients';
    if (this.discharged) {
      routeTo = 'history';
      this.headerService.navUrl = 'app/patients/discharged/true';
    }

    this.router.navigate([`app/patients/${routeTo}`], {
      queryParams: { caseNumber: this.patient.CaseNumber },
    });
  }

    saveChanges(updatedTheatrePatient: TheatrePatient): Observable<void> {
      return new Observable((observer) => {
        this.theatrePatientStorageService.getPatientById(updatedTheatrePatient.Id).subscribe((theatrePatient) => {
          this.theatrePatientStorageService.mapFromTheatrePatient(theatrePatient, 0).subscribe(async (mappedTheatrePatient) => {
            this.queueStorageService.ensureOnlyOneItemPerTheatreQueueType(mappedTheatrePatient.caseNumber, QueueType.TheatreUpdateQueue).subscribe(x => {
              this.theatrePatientService.update(mappedTheatrePatient).subscribe(
                () => {
                  observer.next();
                },
                () => {
                  observer.error('Failed to update theatre patient');
                }
              );
            });
          });
        });
      });
    }
}
